import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { typeIcon, effectiveType, displayName, isHiddenField, TYPE_OPTIONS, AGG_CHOICES, defaultAggFor, setFieldDrag, isNumericType, isDateType } from "@/lib/fieldWells";
import { CalcFieldDialog } from "@/components/bi/CalcEditors";
import { Search, ChevronRight, ChevronDown, Table2, MoreVertical, Eye, EyeOff, Pencil, Sigma, ListTree, FunctionSquare, Columns3, Plus, ArrowUpAZ, ArrowDownZA, Filter } from "lucide-react";
import { cn } from "@/lib/utils";

const stripModel = (data) => ({
  name: data.name || "Model", description: data.description || "", time_dimension: data.time_dimension || null,
  dimensions: data.dimensions || [], measures: data.measures || [], hierarchies: data.hierarchies || [],
  rls_rules: data.rls_rules || [], relationships: data.relationships || [], field_meta: data.field_meta || {},
});

// Persistent field explorer: tables -> measures / fields / hierarchies with search,
// drag-drop, rename, hide, type override and default aggregation.
// Session-scoped explorer state (item 1: search + expand/collapse survive widget
// switches, panel toggles and inspector navigation within the builder session).
const SS_KEY = "eip.datapane.state";
const loadPaneState = () => {
  try { return JSON.parse(sessionStorage.getItem(SS_KEY)) || {}; } catch { return {}; }
};
const savePaneState = (patch) => {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify({ ...loadPaneState(), ...patch })); } catch { /* ignore */ }
};

export const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "bool", label: "True/False" },
  { value: "calc", label: "ƒx" },
];

export function DataPane({ datasets, usedDatasetIds, onDatasetsChanged, selectedWidget = null, onFieldAction = null }) {
  const saved = loadPaneState();
  const [q, setQ] = useState(saved.q || "");
  const [typeFilter, setTypeFilter] = useState(saved.typeFilter || "all");
  const [showHidden, setShowHidden] = useState(!!saved.showHidden);
  const [openIds, setOpenIds] = useState(() => new Set(Array.isArray(saved.openIds) && saved.openIds.length
    ? saved.openIds
    : (usedDatasetIds.length ? usedDatasetIds : datasets.slice(0, 1).map((d) => d.id))));
  useEffect(() => { savePaneState({ q, typeFilter, showHidden, openIds: [...openIds] }); }, [q, typeFilter, showHidden, openIds]);
  const [models, setModels] = useState({});   // dataset_id -> model doc
  const [renameState, setRenameState] = useState(null); // {datasetId, field, value}
  const [calcState, setCalcState] = useState(null);     // {dataset, editField}

  const loadModel = (dsId) => {
    if (models[dsId]) return;
    api.get(`/datasets/${dsId}/model`).then(({ data }) => setModels((m) => ({ ...m, [dsId]: data }))).catch(() => {});
  };
  useEffect(() => { openIds.forEach(loadModel); }, [openIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = (dsId) => setOpenIds((s) => {
    const n = new Set(s);
    n.has(dsId) ? n.delete(dsId) : n.add(dsId);
    return n;
  });

  const saveFieldMeta = async (dsId, field, patch) => {
    const model = models[dsId];
    if (!model) return;
    const fm = { ...(model.field_meta || {}) };
    fm[field] = { ...(fm[field] || {}), ...patch };
    Object.keys(fm[field]).forEach((k) => { if (fm[field][k] === null || fm[field][k] === "" || fm[field][k] === undefined) delete fm[field][k]; });
    if (!Object.keys(fm[field]).length) delete fm[field];
    const body = { ...stripModel(model), field_meta: fm };
    try {
      const { data } = await api.put(`/datasets/${dsId}/model`, body);
      setModels((m) => ({ ...m, [dsId]: data }));
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only the dataset owner can edit field settings" : (e?.response?.data?.detail || "Failed to save"));
    }
  };

  const ql = q.trim().toLowerCase();
  return (
    <div className="h-full flex flex-col" data-testid="data-pane">
      <div className="p-2 shrink-0 space-y-1.5">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid="data-pane-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields…" className="h-8 pl-8 text-xs bg-[color:var(--surface)]" />
        </div>
        <button data-testid="data-pane-show-hidden" onClick={() => setShowHidden((v) => !v)}
          className="text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] flex items-center gap-1">
          {showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}{showHidden ? "Showing hidden fields" : "Show hidden fields"}
        </button>
        <div className="flex items-center gap-1 flex-wrap" data-testid="data-pane-type-filter">
          {TYPE_FILTERS.map((t) => (
            <button key={t.value} data-testid={`data-pane-type-${t.value}`} onClick={() => setTypeFilter(t.value)}
              className={cn("px-1.5 py-0.5 rounded-full text-[10px] border transition-colors",
                typeFilter === t.value
                  ? "border-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.08)] text-[color:var(--brand-ocean)] font-semibold"
                  : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]")}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-4 space-y-1">
          {datasets.map((ds) => (
            <DatasetSection key={ds.id} ds={ds} model={models[ds.id]} open={openIds.has(ds.id) || !!ql}
              onToggle={() => toggleOpen(ds.id)} q={ql} showHidden={showHidden} typeFilter={typeFilter}
              onFieldMeta={(field, patch) => saveFieldMeta(ds.id, field, patch)}
              onRename={(field, current) => setRenameState({ datasetId: ds.id, field, value: current })}
              onCalcField={(editField) => setCalcState({ dataset: ds, editField })}
              selectedWidget={selectedWidget && selectedWidget.dataset_id === ds.id ? selectedWidget : null}
              onFieldAction={onFieldAction} />
          ))}
          {!datasets.length && <p className="text-xs text-[color:var(--ink-3)] p-3 text-center">No datasets yet — add one from the Datasets page.</p>}
        </div>
      </ScrollArea>

      <CalcFieldDialog open={!!calcState} onOpenChange={(v) => !v && setCalcState(null)}
        dataset={calcState?.dataset} editField={calcState?.editField}
        onSaved={() => { setCalcState(null); onDatasetsChanged && onDatasetsChanged(); }} />

      <Dialog open={!!renameState} onOpenChange={(v) => !v && setRenameState(null)}>
        <DialogContent className="max-w-sm" data-testid="field-rename-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-4 h-4 text-[color:var(--brand-ocean)]" />Rename field</DialogTitle></DialogHeader>
          <p className="text-xs text-[color:var(--ink-3)]">Display name only — the source column <span className="font-mono">{renameState?.field}</span> is unchanged.</p>
          <Input data-testid="field-rename-input" value={renameState?.value || ""} onChange={(e) => setRenameState((s) => ({ ...s, value: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { saveFieldMeta(renameState.datasetId, renameState.field, { display_name: renameState.value.trim() || null }); setRenameState(null); } }}
            className="bg-[color:var(--surface)]" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameState(null)}>Cancel</Button>
            <Button data-testid="field-rename-save" className="bg-[color:var(--brand-ocean)] text-white"
              onClick={() => { saveFieldMeta(renameState.datasetId, renameState.field, { display_name: renameState.value.trim() || null }); setRenameState(null); }}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Group({ icon: Icon, label, count, children, defaultOpen = true, extra }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  if (!count && !extra) return null;
  // Keep wide tables (hundreds of columns) responsive: render a slice until asked for all.
  const kids = React.Children.toArray(children);
  const shown = showAll ? kids : kids.slice(0, 150);
  return (
    <div className="ml-1">
      <div className="flex items-center">
        <button onClick={() => setOpen((v) => !v)} className="flex-1 flex items-center gap-1 px-1 py-1 text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <Icon className="w-3 h-3" />{label} · {count}
        </button>
        {extra}
      </div>
      {open && (
        <div className="space-y-px">
          {shown}
          {kids.length > shown.length && (
            <button data-testid="data-pane-show-all" onClick={() => setShowAll(true)}
              className="w-full text-left pl-6 py-1 text-[10px] text-[color:var(--brand-ocean)] hover:underline">
              Show all {kids.length} fields…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DatasetSection({ ds, model, open, onToggle, q, showHidden, typeFilter = "all", onFieldMeta, onRename, onCalcField, selectedWidget = null, onFieldAction = null }) {
  const schema = ds.schema || [];
  const fm = model?.field_meta || {};
  const calcNames = new Set((ds.calc_fields || []).map((f) => f.name));
  const measures = (model?.measures || []).filter((m) => m.visible !== false);
  const hierarchies = model?.hierarchies || [];
  const [openDates, setOpenDates] = useState(() => new Set());

  const match = (label, name) => !q || label.toLowerCase().includes(q) || (name || "").toLowerCase().includes(q);
  const typeOk = (eff, name) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "calc") return calcNames.has(name);
    if (typeFilter === "number") return isNumericType(eff);
    if (typeFilter === "date") return isDateType(eff);
    if (typeFilter === "bool") return eff === "boolean" || eff === "bool";
    return !isNumericType(eff) && !isDateType(eff) && eff !== "boolean" && eff !== "bool";
  };
  const fields = useMemo(() => schema
    .filter((c) => (showHidden || !isHiddenField(c.name, fm)) && match(displayName(c.name, fm), c.name))
    .map((c) => ({ ...c, eff: effectiveType(c.name, schema, fm), disp: displayName(c.name, fm), hidden: isHiddenField(c.name, fm) }))
    .filter((c) => typeOk(c.eff, c.name)),
    [schema, fm, q, showHidden, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  // Bold BI-style split: numeric fields live under Measures (unless "Do Not Summarize"),
  // everything else — plus calc columns and date hierarchies — under Dimensions.
  const measureFields = fields.filter((c) => isNumericType(c.eff) && defaultAggFor(c.name, schema, fm) !== "none");
  const dimensionFields = fields.filter((c) => !measureFields.includes(c));
  const meas = measures.filter((m) => match(m.label || m.key, m.field)
    && (typeFilter === "all" || typeFilter === "number" || (typeFilter === "calc" && m.kind === "calculated")));
  const hiers = typeFilter === "all" ? hierarchies.filter((h) => match(h.label || h.key || "", "")) : [];

  const toggleDate = (name) => setOpenDates((s) => {
    const n = new Set(s);
    n.has(name) ? n.delete(name) : n.add(name);
    return n;
  });

  const fieldMenu = (c) => {
    const calcDef = calcNames.has(c.name) ? (ds.calc_fields || []).find((f) => f.name === c.name) : null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="field-menu-btn" onClick={(e) => e.stopPropagation()} className="p-0.5 rounded opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><MoreVertical className="w-3 h-3" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem data-testid="field-menu-rename" onClick={() => onRename(c.name, c.disp)}><Pencil className="w-3.5 h-3.5 mr-2" />Rename…</DropdownMenuItem>
          {calcDef && <DropdownMenuItem data-testid="field-menu-edit-calc" onClick={() => onCalcField(calcDef)}><FunctionSquare className="w-3.5 h-3.5 mr-2" />Edit expression…</DropdownMenuItem>}
          <DropdownMenuItem data-testid="field-menu-hide" onClick={() => onFieldMeta(c.name, { hidden: !c.hidden })}>
            {c.hidden ? <Eye className="w-3.5 h-3.5 mr-2" /> : <EyeOff className="w-3.5 h-3.5 mr-2" />}{c.hidden ? "Unhide" : "Hide"}
          </DropdownMenuItem>
          {selectedWidget && onFieldAction && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-[color:var(--ink-3)] font-normal">On “{selectedWidget.title || selectedWidget.type}”</DropdownMenuLabel>
              <DropdownMenuItem data-testid="field-menu-sort-asc" onClick={() => onFieldAction("sort", { field: c.name, direction: "asc", agg: defaultAggFor(c.name, schema, fm), numeric: isNumericType(c.eff) })}><ArrowUpAZ className="w-3.5 h-3.5 mr-2" />Sort ascending</DropdownMenuItem>
              <DropdownMenuItem data-testid="field-menu-sort-desc" onClick={() => onFieldAction("sort", { field: c.name, direction: "desc", agg: defaultAggFor(c.name, schema, fm), numeric: isNumericType(c.eff) })}><ArrowDownZA className="w-3.5 h-3.5 mr-2" />Sort descending</DropdownMenuItem>
              <DropdownMenuItem data-testid="field-menu-add-filter" onClick={() => onFieldAction("filter", { field: c.name, numeric: isNumericType(c.eff), date: isDateType(c.eff) })}><Filter className="w-3.5 h-3.5 mr-2" />Add as filter…</DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="field-menu-type">Data type · <span className="ml-1 text-[color:var(--brand-ocean)]">{c.eff}</span></DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {TYPE_OPTIONS.map((t) => (
                <DropdownMenuItem key={t} data-testid={`field-type-${t}`} onClick={() => onFieldMeta(c.name, { type_override: t === c.type ? null : t })}>
                  {t}{t === c.eff ? " ✓" : ""}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="field-menu-agg">Default aggregation · <span className="ml-1 text-[color:var(--brand-ocean)]">{defaultAggFor(c.name, schema, fm) === "none" ? "don't summarize" : defaultAggFor(c.name, schema, fm)}</span></DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {AGG_CHOICES.map((a) => (
                <DropdownMenuItem key={a} data-testid={`field-agg-${a}`} onClick={() => onFieldMeta(c.name, { default_agg: a })}>
                  {a}{a === defaultAggFor(c.name, schema, fm) ? " ✓" : ""}
                </DropdownMenuItem>
              ))}
              {isNumericType(c.eff) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="field-agg-none" onClick={() => onFieldMeta(c.name, { default_agg: "none" })}>
                    Do Not Summarize{defaultAggFor(c.name, schema, fm) === "none" ? " ✓" : ""}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderField = (c, { asMeasure = false } = {}) => {
    const isDate = isDateType(c.eff);
    const Icon = calcNames.has(c.name) ? FunctionSquare : (asMeasure ? Sigma : typeIcon(c.eff));
    const row = (
      <FieldRow key={c.name} testid={asMeasure ? "data-pane-numeric-field" : "data-pane-field"} icon={Icon} label={c.disp} dim={c.hidden}
        sub={asMeasure ? defaultAggFor(c.name, schema, fm) : (calcNames.has(c.name) ? "ƒx" : (fm[c.name]?.type_override ? c.eff : null))}
        expander={isDate ? (
          <button data-testid="date-field-expand" onClick={(e) => { e.stopPropagation(); toggleDate(c.name); }}
            className="p-0.5 -ml-5 mr-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
            {openDates.has(c.name) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : null}
        drag={{ dataset_id: ds.id, kind: "field", field: c.name, data_type: c.eff, default_agg: defaultAggFor(c.name, schema, fm), label: c.disp }}
        menu={fieldMenu(c)} />
    );
    if (!isDate) return row;
    return (
      <React.Fragment key={c.name}>
        {row}
        {openDates.has(c.name) && DATE_HIERARCHY.map((p) => (
          <FieldRow key={`${c.name}-${p.part}`} testid="data-pane-date-part" icon={typeIcon("date")} label={p.label} indent
            drag={{ dataset_id: ds.id, kind: "field", field: c.name, date_part: p.part, data_type: c.eff, default_agg: "count", label: `${c.disp} (${p.label})` }} />
        ))}
      </React.Fragment>
    );
  };

  return (
    <div data-testid="data-pane-table" className="rounded-lg">
      <button onClick={onToggle} className="w-full flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg hover:bg-[color:var(--surface-2)] text-left">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-[color:var(--ink-3)]" /> : <ChevronRight className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />}
        <Table2 className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />
        <span className="text-xs font-semibold text-[color:var(--ink)] truncate flex-1">{ds.name}</span>
        <span className="text-[10px] text-[color:var(--ink-3)]">{schema.length}</span>
      </button>
      {open && (
        <div className="pb-1">
          {!meas.length && !measureFields.length && !dimensionFields.length && !hiers.length && (
            <p data-testid="data-pane-no-fields" className="text-[10px] text-[color:var(--ink-3)] px-6 py-1.5">No fields match this search or type filter.</p>
          )}
          <Group icon={Sigma} label="Measures" count={meas.length + measureFields.length}>
            {meas.map((m) => (
              <FieldRow key={m.key} testid="data-pane-measure" icon={typeIcon(m.kind === "calculated" ? "calculated" : "measure")}
                label={m.label || m.key} sub={m.kind === "calculated" ? (m.result_type && m.result_type !== "number" && m.result_type !== "unknown" ? `ƒx · ${m.result_type}` : "ƒx") : m.agg}
                drag={{ dataset_id: ds.id, kind: "measure", key: m.key, field: m.field || "", agg: m.agg || "sum", label: m.label || m.key }} />
            ))}
            {measureFields.map((c) => renderField(c, { asMeasure: true }))}
          </Group>
          <Group icon={Columns3} label="Dimensions" count={dimensionFields.length}
            extra={
              <button data-testid="new-calc-field-btn" title="New calculated field"
                onClick={(e) => { e.stopPropagation(); onCalcField(null); }}
                className="p-0.5 rounded text-[color:var(--brand-mint)] hover:bg-[color:var(--surface-2)]">
                <Plus className="w-3 h-3" />
              </button>
            }>
            {dimensionFields.map((c) => renderField(c))}
          </Group>
          <Group icon={ListTree} label="Hierarchies" count={hiers.length} defaultOpen={false}>
            {hiers.map((h, i) => (
              <FieldRow key={h.key || i} testid="data-pane-hierarchy" icon={typeIcon("hierarchy")} label={h.label || h.key} sub={`${(h.levels || []).length} levels`}
                drag={{ dataset_id: ds.id, kind: "hierarchy", levels: h.levels || [], label: h.label || h.key }} />
            ))}
          </Group>
        </div>
      )}
    </div>
  );
}

const DATE_HIERARCHY = [
  { part: "year", label: "Year" }, { part: "quarter_of_year", label: "Quarter" },
  { part: "month_of_year", label: "Month" }, { part: "week", label: "Week" },
  { part: "day", label: "Day" }, { part: "quarter", label: "Quarter year" },
  { part: "month", label: "Month year" }, { part: "week_year", label: "Week year" },
  { part: "day_of_week", label: "Day of week" }, { part: "week_of_year", label: "Week of year" },
  { part: "date_hour", label: "Date hour" },
];

function FieldRow({ icon: Icon, label, sub, drag, menu, dim, testid, expander, indent }) {
  return (
    <div data-testid={testid} draggable onDragStart={(e) => setFieldDrag(e, drag)}
      className={cn("group flex items-center gap-1.5 pr-1 py-1 rounded-md cursor-grab text-xs hover:bg-[color:var(--surface-2)] active:cursor-grabbing", indent ? "pl-11" : "pl-6", dim && "opacity-45")}>
      {expander}
      <Icon className="w-3 h-3 shrink-0 text-[color:var(--brand-ocean)]" />
      <span className="truncate flex-1 text-[color:var(--ink-2)]">{label}</span>
      {sub && <span className="text-[9px] text-[color:var(--ink-3)] shrink-0">{sub}</span>}
      {menu}
    </div>
  );
}
